/**
 * Jumping, counted (CAM-03, game design spec §15's "one jump must produce one count").
 *
 * A jump is not a threshold crossing; it is a whole shape: the hips rise clear of the calibrated
 * standing baseline, and then come back. The event is reported on the **return**, which is what
 * makes "one action → one event" true rather than hoped for — a child hanging at the top of a jump
 * for four frames has not made four jumps, and a child who rises, wobbles and comes down has made
 * one.
 *
 * Two windows guard the count, both from `GameplayTunables.detector`:
 *   - `actionCooldownMs` — the quiet period after a counted jump, which is what absorbs the bounce
 *     on landing (the classic double-count);
 *   - `actionDebounceMs` — a minimum gap between counted jumps, a backstop that still bites if the
 *     cooldown is ever tuned to nothing.
 *
 * A crouch cannot be mistaken for a jump, because the hips move the other way and the rise is
 * measured with a sign.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { hipMidpoint } from '../pose-geometry';
import { POSE_LANDMARK } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const JUMP_LANDMARKS = [
  POSE_LANDMARK.leftHip,
  POSE_LANDMARK.rightHip,
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
];

export function createJumpDetector(options: DetectorOptions = {}): PoseDetector {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: JUMP_LANDMARKS, tunables });

  const takeOffRise = calibration.bodyScale * calibration.jumpRiseScale;
  const landingRise = takeOffRise * calibration.hysteresisFraction;

  let airborne = false;
  /** When the last counted jump *left the ground*: the cooldown is timed from there. */
  let lastTakeOffAt = Number.NEGATIVE_INFINITY;
  /** When the last counted jump landed: the debounce is timed from there. */
  let lastCountedAt = Number.NEGATIVE_INFINITY;
  let takeOffAt = Number.NEGATIVE_INFINITY;
  /**
   * Neutral re-acquisition (D-068(2)): after a reset or a loss the body must be seen back on the
   * ground before a new take-off can arm.
   *
   * An action that spans a reset is **discarded**, in both directions. Without this, a reset while
   * the child is in the air makes the recovered raised pose a fresh take-off, and its landing
   * counts a repetition nobody watched begin — a round-context switch or a tracking recovery could
   * charge a web on its own. Discarding costs a child one jump they can simply make again.
   */
  let awaitingNeutral = false;

  return {
    capability: 'jump',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        // A jump nobody watched land is not a jump. Whatever the body does next starts over — and
        // "over" begins on the ground, or the body coming back into view is itself a take-off.
        airborne = false;
        awaitingNeutral = true;
        return reading.outcome;
      }

      const hips = hipMidpoint(reading.pose);
      if (hips === null) return { kind: 'none' };

      // Frame space grows downward, so a rise is the baseline minus the current hip height.
      const rise = calibration.standingHipY - hips.y;

      if (awaitingNeutral) {
        // Nothing is counted and nothing is armed until the child is genuinely back down.
        if (rise > landingRise) return { kind: 'none' };
        awaitingNeutral = false;
        return { kind: 'none' };
      }

      if (!airborne) {
        // The cooldown runs from where the counted jump *started*, not from where it landed. A
        // jump occupies both, so timing the quiet period from the landing would refuse the second
        // of two ordinary jumps while still leaving the landing bounce — which is the only thing
        // the cooldown exists to catch — comfortably inside the window.
        const restingAfterCount = reading.at - lastTakeOffAt < tunables.detector.actionCooldownMs;
        if (rise >= takeOffRise && !restingAfterCount) {
          airborne = true;
          takeOffAt = reading.at;
        }
        return { kind: 'none' };
      }

      if (rise > landingRise) return { kind: 'none' };

      airborne = false;
      if (reading.at - lastCountedAt < tunables.detector.actionDebounceMs) return { kind: 'none' };

      lastCountedAt = reading.at;
      lastTakeOffAt = takeOffAt;
      return {
        kind: 'event',
        event: { type: 'PLAYER_JUMPED' },
        confidence: reading.confidence,
      };
    },

    /**
     * The jump in the air is discarded; the ground it must return to is still the ground (S2-3).
     *
     * **Only when there is actually a jump in the air (S3-2).** Arming the neutral gate on a
     * detector that was already standing still is not a discard — it is the one-frame action loss
     * that D-070 confined to `reset()`, and it swallowed a whole jump from a child who was neutral
     * at Resume and took off before the next camera frame. A keyboard action begun after Resume
     * emits normally, so this one must too.
     */
    discardInProgress(): void {
      if (!airborne) return;
      airborne = false;
      awaitingNeutral = true;
    },

    /** A hole in the observation: a whole jump can happen inside one (S5-1/S5-3/S5-5). */
    reacquireSilently(): void {
      airborne = false;
      awaitingNeutral = true;
      takeOffAt = Number.NEGATIVE_INFINITY;
      lastTakeOffAt = Number.NEGATIVE_INFINITY;
      lastCountedAt = Number.NEGATIVE_INFINITY;
    },

    reset(): void {
      gate.reset();
      airborne = false;
      awaitingNeutral = true;
      takeOffAt = Number.NEGATIVE_INFINITY;
      lastTakeOffAt = Number.NEGATIVE_INFINITY;
      lastCountedAt = Number.NEGATIVE_INFINITY;
    },
  };
}
